import type { Router, RouteRecordNormalized } from 'vue-router'
import { getRouteTree } from '@/router/routeTree'

/**
 * The pages a person can jump to by name: the header search lists them, and the sidebar's
 * "Add item" picker offers them. Kept in one place so the two can never disagree about what
 * counts as a page.
 */

/**
 * A section parent whose empty-path child only redirects to one of its tabs. The parent is not a
 * page of its own -- opening it lands on a tab -- so only the tabs are listed.
 */
export function hasDefaultRedirectIndex (record: RouteRecordNormalized): boolean {
  const defaultChild = (record.children ?? []).find(child => child.path === '' || child.path === '/')
  return !!(defaultChild && defaultChild.redirect)
}

/**
 * Every record that is a page in its own right, without regard to who may open it. Callers that
 * show the list to someone filter it with `hasAccessToRouteName`.
 */
export function getSearchableRouteRecords (router: Router): RouteRecordNormalized[] {
  return [...getRouteTree(router).byName.values()].filter(record =>
    record.meta?.label &&
    !record.redirect &&
    record.meta.searchable !== false &&
    !hasDefaultRedirectIndex(record)
  )
}

/** The trail people see for a page, e.g. "Attendance / Time Cards / Editor". */
export function getRoutePathString (router: Router, record: RouteRecordNormalized): string {
  return getRouteTree(router).ancestry(record)
    .map(current => (current.meta?.label || current.meta?.title) as string | undefined)
    .filter((label): label is string => !!label && label.toLowerCase().trim() !== 'home')
    .reverse()
    .join(' / ')
}

/**
 * The name a page goes by out of context, where its tab label alone would be ambiguous: the
 * time card editor's tab just says "Editor". Falls back to the longer page title, then the label.
 */
export function getRouteNavLabel (record: RouteRecordNormalized): string {
  return (record.meta?.navLabel || record.meta?.title || record.meta?.label || String(record.name ?? '')) as string
}
